import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {FilterBasicLegacySingleSelection} from '@adv-mt/mt-filter-basic-legacy'
import FilterWrapperBasic, {filterWrapperBasicAllSelectionsId as allSelectionsId} from '@adv-mt/mt-filter-wrapper-basic'
import Select, {formSelectSizes} from '@adv-mt/mt-form-select'

const DEFAULT_EMPTY_TOTAL_RESULTS = 0

const LEGACY_WRAPPER = 'legacy'

const FormTechnicalMake = ({
  aggregations,
  initialSearch = {},
  isBasicContent = false,
  isMultiMakeModelVersion = false,
  hasErrors = false,
  onChange,
  options = [],
  overlayContentRef,
  pageName = '',
  type,
  value = 0,
  vehiclePosition,
  withLabel = true,
  wrapper = LEGACY_WRAPPER
}) => {
  const {domain, i18n} = useContext(Context)

  const config = domain.get('config')
  const {FILTER_NAMES} = config.get('search')
  const {DEFAULT_VEHICLE_SEARCH_VALUE} = config.get('searchFilters')
  const TRACKING_FILTER_NAMES = config.get('TRACKING_FILTER_NAMES')
  const {
    TECHNICAL_DETAILS_SEARCH_PAGE_NAME,
    TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME,
    MAKES_DISTRIBUTOR_PAGE_NAME,
    MAKES_SIZES_DISTRIBUTOR_PAGE_NAME
  } = config.get('page')
  const {VEHICLE_SPECS_CONTEXT, VEHICLE_SPEC_PARAMS} = config.get('vehicles')
  const [makes, setMakes] = useState([])
  const {categoryId} = initialSearch
  const placeholder = i18n.t('COMMON_ALL_MAKES')

  const isTechnicalDetailsSearchPage =
    pageName === TECHNICAL_DETAILS_SEARCH_PAGE_NAME || pageName === TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME

  const isMakesDistributorPage =
    pageName === MAKES_DISTRIBUTOR_PAGE_NAME || pageName === MAKES_SIZES_DISTRIBUTOR_PAGE_NAME

  const hasAggregations = Boolean(aggregations)

  const getExtendedParams = () => {
    if (isTechnicalDetailsSearchPage) return {}
    if (isMakesDistributorPage) return VEHICLE_SPEC_PARAMS.MARKETED_ITEMS
    return VEHICLE_SPEC_PARAMS.MANUAL_ITEMS
  }

  const extendedParams = getExtendedParams()

  const getItemTotalResults = ({itemId}) =>
    aggregations.find(({id}) => String(id) === String(itemId))?.totalResults || DEFAULT_EMPTY_TOTAL_RESULTS

  const mapItem = ({id, label}) => ({
    id: id && String(id),
    label,
    ...(hasAggregations &&
      // Only show extended info if id is not the default all selections options id (zero value)
      id && {
        extendedInfo: i18n.n(Number(getItemTotalResults({itemId: id})))
      })
  })

  const onMakeChange = (e, {value}) => {
    const selectedMake = makes.find(make => make.id === value)
    const nextValue = selectedMake && {
      id: selectedMake.id,
      name: selectedMake.label
    }
    onChange(e, {
      field: FILTER_NAMES.MAKE,
      value: nextValue,
      lastFilter: TRACKING_FILTER_NAMES[FILTER_NAMES.MAKE]
    })
  }

  useMount(async () => {
    // if options are provided, avoid call to UC to retrieve makes
    if (options && options.length > 0) {
      const currentMakes = options.map(mapItem)
      setMakes(currentMakes)
      return
    }

    const [error, response] = await domain.get('get_specs_vehicles_use_case').execute({
      nextField: VEHICLE_SPECS_CONTEXT.MAKE,
      params: {
        section1Id: categoryId,
        ...extendedParams
      }
    })

    if (error) return

    const {items} = response
    const parsedItems = items.map(({value, text}) => mapItem({id: value, label: text}))
    setMakes(parsedItems)
  })

  const handleChangeMake = ({id}) => onMakeChange(null, {value: id})

  const handleChange = ({value}) => {
    const [firstValue] = value
    onMakeChange(null, {value: firstValue === allSelectionsId ? DEFAULT_VEHICLE_SEARCH_VALUE.makeId : firstValue})
  }

  const handleReset = ({filters}) => {
    const [filterName] = filters
    onChange(null, {field: filterName, value: DEFAULT_VEHICLE_SEARCH_VALUE.makeId})
  }

  if (wrapper === LEGACY_WRAPPER) {
    return isBasicContent ? (
      <div className="mt-FormMakeBasicContent">
        <FilterBasicLegacySingleSelection onChange={handleChangeMake} options={makes} value={String(value)} />
      </div>
    ) : (
      <Select
        aggregations={aggregations}
        field={FILTER_NAMES.MAKE}
        initialSearch={initialSearch}
        isMultiMakeModelVersion={isMultiMakeModelVersion}
        label={i18n.t('COMMON.MAKE')}
        onChange={onMakeChange}
        options={makes}
        placeholder={placeholder}
        hasErrors={hasErrors}
        overlayContentRef={overlayContentRef}
        pageName={pageName}
        size={formSelectSizes.LARGE}
        value={value}
        vehiclePosition={vehiclePosition}
      />
    )
  }

  const defaultPlaceholder = withLabel ? i18n.t('COMMON_ALL_MAKES') : i18n.t('COMMON.MAKE')

  const currentPlaceholder = value ? makes.find(({id}) => String(id) === String(value))?.label : defaultPlaceholder

  return (
    <FilterWrapperBasic
      filters={[
        {
          allSelectionsLabel: i18n.t('COMMON_ALL_MAKES'),
          name: FILTER_NAMES.MAKE,
          options: makes,
          type
        }
      ]}
      group={`${FILTER_NAMES.MAKE}Group`}
      legend={i18n.t('COMMON.MAKE')}
      onChange={handleChange}
      onReset={handleReset}
      overlayContentRef={overlayContentRef}
      placeholder={currentPlaceholder}
      value={{[FILTER_NAMES.MAKE]: [value ? String(value) : allSelectionsId]}}
      wrapper={wrapper}
    />
  )
}

const FormTechnicalMakeBasicContent = props => <FormTechnicalMake {...props} isBasicContent />

FormTechnicalMakeBasicContent.displayName = 'FormTechnicalMakeBasicContent'

FormTechnicalMake.displayName = 'FormTechnicalMake'
FormTechnicalMake.propTypes = {
  aggregations: PropTypes.array,
  initialSearch: PropTypes.shape({
    categoryId: PropTypes.number
  }),
  hasErrors: PropTypes.bool,
  isBasicContent: PropTypes.bool,
  isMultiMakeModelVersion: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  overlayContentRef: PropTypes.node,
  pageName: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.number,
  vehiclePosition: PropTypes.string,
  withLabel: PropTypes.bool,
  wrapper: PropTypes.string
}

export {FormTechnicalMakeBasicContent}

export default FormTechnicalMake
